<template>
  <article class="dashboard-card preika-insight-card preika-work-projection">
    <div class="preika-insight-heading">
      <div>
        <p class="preika-insight-heading__eyebrow">MONTH-END FORECAST</p>
        <h2>月末勤務着地予測</h2>
        <p class="preika-insight-heading__description">
          現在までの実績ペースから月末の勤務時間を予測します。
        </p>
      </div>
      <span class="preika-insight-badge" :class="forecastToneClass">
        {{ forecastLabel }}
      </span>
    </div>

    <div v-if="loading" class="preika-insight-loading">
      <span class="preika-insight-loading__spinner"></span>
      勤務実績を分析しています
    </div>

    <div v-else-if="error" class="preika-insight-error" role="alert">
      <div>
        <strong>勤務着地予測を取得できませんでした</strong>
        <small>勤務表と月次集計の表示には影響しません。</small>
      </div>
      <button type="button" @click="$emit('retry')">再取得</button>
    </div>

    <template v-else>
      <div class="preika-forecast-kpis">
        <div class="preika-forecast-kpi">
          <small>現在実績</small>
          <strong>{{ formatMinutes(value.actualMinutes) }}</strong>
          <span>{{ formatDays(value.workedDays) }}日稼働</span>
        </div>
        <div class="preika-forecast-kpi preika-forecast-kpi--primary">
          <small>月末予測</small>
          <strong>{{ formatMinutes(value.projectedMinutes) }}</strong>
          <span>現時点のペース</span>
        </div>
        <div class="preika-forecast-kpi">
          <small>所定時間</small>
          <strong>{{ formatMinutes(value.definedMinutes) }}</strong>
          <span>{{ formatDays(value.definedWorkDays) }}日想定</span>
        </div>
        <div class="preika-forecast-kpi" :class="forecastOvertimeClass">
          <small>予測時間外</small>
          <strong>{{ formatMinutes(value.projectedOvertimeMinutes) }}</strong>
          <span>月末時点</span>
        </div>
      </div>

      <div class="preika-forecast-chart" :class="{ 'preika-forecast-chart--empty': !trend.length }">
        <div class="preika-forecast-chart__legend">
          <span><i class="preika-forecast-legend preika-forecast-legend--actual"></i>実績</span>
          <span><i class="preika-forecast-legend preika-forecast-legend--forecast"></i>予測</span>
          <span><i class="preika-forecast-legend preika-forecast-legend--target"></i>所定</span>
        </div>

        <svg
          v-if="trend.length"
          viewBox="0 0 600 220"
          preserveAspectRatio="none"
          role="img"
          aria-label="月内の累積勤務時間と月末予測"
        >
          <line v-for="line in gridLines" :key="line" x1="44" x2="570" :y1="line" :y2="line" class="preika-forecast-gridline"/>
          <line x1="44" x2="570" :y1="targetY" :y2="targetY" class="preika-forecast-target-line"/>
          <polyline :points="actualPolyline" class="preika-forecast-actual-line"/>
          <polyline v-if="projectionPolyline" :points="projectionPolyline" class="preika-forecast-projection-line"/>
          <circle v-if="lastActualPoint" :cx="lastActualPoint.x" :cy="lastActualPoint.y" r="5" class="preika-forecast-current-point"/>
          <circle cx="570" :cy="projectedY" r="5" class="preika-forecast-end-point"/>
          <text x="44" y="211" class="preika-forecast-axis-label">月初</text>
          <text x="526" y="211" class="preika-forecast-axis-label">月末</text>
        </svg>

        <div v-else class="preika-insight-empty preika-insight-empty--compact">
          <span class="preika-insight-empty__icon"><CIcon name="cil-chart-pie"/></span>
          <div>
            <strong>勤務実績が蓄積されると予測線を表示します</strong>
            <p>所定時間と現在値は上部の指標で確認できます。</p>
          </div>
        </div>
      </div>

      <div class="preika-forecast-guidance">
        <div>
          <small>残り稼働日</small>
          <strong>{{ formatDays(value.remainingWorkDays) }}日</strong>
        </div>
        <div>
          <small>所定時間へ到達する1日平均</small>
          <strong>{{ formatMinutes(value.requiredDailyMinutes) }}</strong>
        </div>
        <div class="preika-forecast-progress">
          <span><small>所定時間進捗</small><strong>{{ progressRate }}%</strong></span>
          <div class="preika-forecast-progress__track">
            <i :style="{ width: progressRate + '%' }"></i>
          </div>
        </div>
      </div>

      <div v-if="managementView && teamRisks.length" class="preika-team-risk">
        <div class="preika-team-risk__heading">
          <div>
            <small>TEAM FORECAST</small>
            <strong>時間外着地の注意メンバー</strong>
          </div>
          <span>{{ teamRisks.length }}名</span>
        </div>
        <div class="preika-team-risk__list">
          <div v-for="risk in teamRisks" :key="risk.userId" class="preika-team-risk__item">
            <span class="preika-team-risk__avatar">{{ userInitial(risk.userName) }}</span>
            <span class="preika-team-risk__person">
              <strong>{{ risk.userName }}</strong>
              <small>{{ risk.departmentName || '未所属' }}</small>
            </span>
            <span class="preika-team-risk__value" :class="'preika-team-risk__value--' + risk.tone">
              +{{ formatMinutes(risk.projectedOvertimeMinutes) }}
            </span>
          </div>
        </div>
      </div>
    </template>
  </article>
</template>

<script>
import {
  clampInsightPercent,
  formatInsightMinutes,
  formatInsightWorkDays
} from '../../utils/dashboardInsights'

export default {
  name: 'DashboardWorkProjection',

  props: {
    projection: {
      type: Object,
      default: () => ({})
    },
    managementView: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    value () {
      return this.projection || {}
    },

    trend () {
      return Array.isArray(this.value.trend) ? this.value.trend : []
    },

    teamRisks () {
      return Array.isArray(this.value.teamRisks) ? this.value.teamRisks : []
    },

    progressRate () {
      return Math.round(clampInsightPercent(this.value.progressRate) * 10) / 10
    },

    forecastLabel () {
      const overtime = Number(this.value.projectedOvertimeMinutes || 0)
      if (overtime >= 1200) return '要調整'
      if (overtime >= 600) return '注意'
      if (Number(this.value.projectedMinutes || 0) > 0) return '順調'
      return '集計待ち'
    },

    forecastToneClass () {
      const overtime = Number(this.value.projectedOvertimeMinutes || 0)
      if (overtime >= 1200) return 'preika-insight-badge--danger'
      if (overtime >= 600) return 'preika-insight-badge--warning'
      return 'preika-insight-badge--success'
    },

    forecastOvertimeClass () {
      const overtime = Number(this.value.projectedOvertimeMinutes || 0)
      if (overtime >= 1200) return 'preika-forecast-kpi--danger'
      if (overtime >= 600) return 'preika-forecast-kpi--warning'
      return ''
    },

    maxChartMinutes () {
      const actualMax = this.trend.reduce((max, item) => Math.max(max, Number(item.cumulativeMinutes || 0)), 0)
      return Math.max(
        actualMax,
        Number(this.value.definedMinutes || 0),
        Number(this.value.projectedMinutes || 0),
        60
      )
    },

    chartDayCount () {
      return Math.max(Number(this.value.definedWorkDays || 0), this.trend.length, 1)
    },

    actualPoints () {
      return this.trend.map((item, index) => ({
        x: 44 + ((index + 1) / this.chartDayCount) * 526,
        y: this.chartY(item.cumulativeMinutes)
      }))
    },

    actualPolyline () {
      return this.actualPoints.map(point => `${point.x},${point.y}`).join(' ')
    },

    lastActualPoint () {
      return this.actualPoints.length ? this.actualPoints[this.actualPoints.length - 1] : null
    },

    projectedY () {
      return this.chartY(this.value.projectedMinutes)
    },

    projectionPolyline () {
      if (!this.lastActualPoint) return ''
      return `${this.lastActualPoint.x},${this.lastActualPoint.y} 570,${this.projectedY}`
    },

    targetY () {
      return this.chartY(this.value.definedMinutes)
    },

    gridLines () {
      return [38, 76, 114, 152, 190]
    }
  },

  methods: {
    formatMinutes (value) {
      return formatInsightMinutes(value)
    },

    formatDays (value) {
      return formatInsightWorkDays(value)
    },

    chartY (value) {
      const numericValue = Math.max(0, Number(value || 0))
      return 190 - (numericValue / this.maxChartMinutes) * 152
    },

    userInitial (name) {
      const value = String(name || '人')
      return value.substring(0, 1)
    }
  }
}
</script>
